/* Pipeline 172X: responsive product shell
   Desktop: persistent 228px sidebar + topbar
   Tablet: collapsible overlay sidebar
   Mobile: bottom navigation bar + full-screen views */

import { useState, useEffect, type ReactNode } from "react";
import { Logo, Badge172 } from "./Logo";
import { BrandWordmark } from "./BrandWordmark";
import {
  IconToday,
  IconJobs,
  IconCampaigns,
  IconApplications,
  IconAnswers,
  IconAnalytics,
  IconAttention,
  IconProfile,
  IconSubscription,
  IconPrivacy,
  IconSecurity,
  IconSettings,
  IconSearch,
  IconMenu,
  IconClose,
} from "./icons";
import type { ExecutionCapability } from "../types";
import { useSession } from "../app/SessionContext";
import { automationGateway } from "../gateways/automationGateway";
import { alphaFeedbackUrl } from "../config";

export type ActiveView =
  | "today"
  | "jobs"
  | "campaigns"
  | "applications"
  | "answers"
  | "analytics"
  | "attention"
  | "exchange"
  | "profile"
  | "subscription"
  | "privacy"
  | "security-sessions"
  | "automation"
  | "onboarding"
  | "package-studio"
  | "pre-submit"
  | "attempt";

interface NavItem {
  id: ActiveView;
  label: string;
  Icon: React.ComponentType<{ size?: number; className?: string }>;
  mobileOrder?: number;
}

const primaryNav: NavItem[] = [
  { id: "exchange", label: "Exchange", Icon: IconJobs },
  { id: "today", label: "Today", Icon: IconToday, mobileOrder: 1 },
  { id: "jobs", label: "Jobs", Icon: IconJobs, mobileOrder: 2 },
  { id: "campaigns", label: "Campaigns", Icon: IconCampaigns, mobileOrder: 3 },
  {
    id: "applications",
    label: "Applications",
    Icon: IconApplications,
    mobileOrder: 4,
  },
  { id: "answers", label: "Answers", Icon: IconAnswers },
  { id: "analytics", label: "Analytics", Icon: IconAnalytics },
];

const secondaryNav: NavItem[] = [
  { id: "attention", label: "Attention", Icon: IconAttention, mobileOrder: 5 },
  { id: "profile", label: "Profile", Icon: IconProfile },
  { id: "automation", label: "Automation", Icon: IconSettings },
  { id: "subscription", label: "Subscription", Icon: IconSubscription },
  { id: "privacy", label: "Privacy", Icon: IconPrivacy },
  { id: "security-sessions", label: "Security & Sessions", Icon: IconSecurity },
];

interface Props {
  activeView: ActiveView;
  onViewChange: (view: ActiveView) => void;
  children: ReactNode;
}

function initials(value: string): string {
  const parts = value
    .split(/[\s@._-]+/)
    .map((part) => part.trim())
    .filter(Boolean);
  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
}

function NavLink({
  item,
  isActive,
  onClick,
  badge,
  compact = false,
}: {
  item: NavItem;
  isActive: boolean;
  onClick: () => void;
  badge?: number;
  compact?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      aria-current={isActive ? "page" : undefined}
      className={`w-full flex items-center gap-2.5 px-2 rounded transition-colors focus-visible:outline-2 focus-visible:outline-offset-1 ${
        compact ? "py-2 justify-center" : "py-1.5"
      } ${
        isActive
          ? "bg-[var(--color-action-subtle)] text-[var(--color-action-primary)]"
          : "text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-raised)]"
      }`}
      style={{ borderRadius: "var(--radius-md)" }}
      title={compact ? item.label : undefined}
    >
      <item.Icon size={15} className="shrink-0" />
      {!compact && (
        <span className="flex-1 text-left text-[12.5px] font-medium">
          {item.label}
        </span>
      )}
      {badge !== undefined && badge > 0 && (
        <span
          className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none ${
            item.id === "attention"
              ? "bg-amber-100 text-amber-800"
              : "bg-[var(--color-action-primary)] text-white"
          } ${compact ? "absolute -top-1 -right-1" : ""}`}
          aria-label={`${badge} items`}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

function ExecutionChip({ capability }: { capability: ExecutionCapability }) {
  const config: Record<
    ExecutionCapability,
    { label: string; dotClass: string; title: string }
  > = {
    unavailable: {
      label: "No execution",
      dotClass: "bg-gray-400",
      title: "No supported submission adapters enabled",
    },
    paused: {
      label: "Paused",
      dotClass: "bg-amber-500",
      title: "All automation globally paused",
    },
    "prepare-only": {
      label: "Prepare only",
      dotClass: "bg-blue-500",
      title: "Preparing packages. No external submissions authorized.",
    },
    assisted: {
      label: "Assisted",
      dotClass: "bg-violet-500",
      title: "Assisted execution enabled for authorized packages",
    },
    authorized: {
      label: "Authorized",
      dotClass: "bg-green-500",
      title: "Controlled attempts authorized for approved packages",
    },
  };
  const c = config[capability];
  return (
    <div
      className="flex items-center gap-1.5 px-2.5 py-1 rounded border text-[11px] font-medium text-[var(--color-text-secondary)]"
      style={{
        background: "var(--color-bg-surface)",
        borderColor: "var(--color-border-default)",
        borderRadius: "var(--radius-md)",
      }}
      title={c.title}
      role="status"
      aria-label={`Execution mode: ${c.label}. ${c.title}`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full shrink-0 ${c.dotClass}`}
        aria-hidden="true"
      />
      {c.label}
    </div>
  );
}

export default function ProductShell({
  activeView,
  onViewChange,
  children,
}: Props) {
  const { user } = useSession();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [automationEnabled, setAutomationEnabled] = useState<boolean | null>(
    null,
  );
  const [automationOperatorConfigured, setAutomationOperatorConfigured] =
    useState(false);

  // The authenticated account already carries display metadata.  Loading a
  // second identity resource solely to decorate shell chrome blocks Today and
  // adds avoidable contention to the first authenticated page.
  const displayName = user?.displayName ?? user?.email ?? "Signed-in account";
  const avatarInitials = initials(displayName);
  const feedbackUrl = alphaFeedbackUrl();
  const executionCapability: ExecutionCapability =
    automationEnabled === true
      ? "authorized"
      : automationOperatorConfigured
        ? "paused"
        : "prepare-only";

  useEffect(() => {
    let active = true;
    void automationGateway
      .getSettings()
      .then((settings) => {
        if (!active) return;
        setAutomationEnabled(settings.effectiveEnabled);
        setAutomationOperatorConfigured(settings.operatorConfigured);
      })
      .catch(() => {
        if (active) setAutomationEnabled(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    function check() {
      setIsMobile(window.innerWidth < 640);
      setIsTablet(window.innerWidth >= 640 && window.innerWidth < 1024);
    }
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Keyboard shortcut for command palette
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setCmdOpen((v) => !v);
      }
      if (e.key === "Escape") {
        setCmdOpen(false);
      }
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const mobileNavItems = [...primaryNav, ...secondaryNav]
    .filter((n) => n.mobileOrder !== undefined)
    .sort((a, b) => (a.mobileOrder ?? 99) - (b.mobileOrder ?? 99));

  const sidebarContent = (compact: boolean = false) => (
    <>
      {/* Logo */}
      <div
        className={`flex items-center border-b ${compact ? "justify-center px-3" : "px-4"}`}
        style={{
          height: "var(--topbar-height)",
          borderColor: "var(--color-border-default)",
        }}
      >
        {compact ? <Badge172 size="sm" /> : <Logo />}
      </div>

      {/* Navigation */}
      <div className="flex-1 py-2 overflow-y-auto">
        <div className={`${compact ? "px-1" : "px-2"} space-y-0.5`}>
          <div
            className={`text-[9px] font-semibold uppercase tracking-wider text-[var(--color-text-subtle)] ${compact ? "text-center py-1.5" : "px-2 py-1.5"}`}
          >
            {compact ? "·" : "Workspace"}
          </div>
          {primaryNav.map((item) => (
            <NavLink
              key={item.id}
              item={item}
              isActive={activeView === item.id}
              onClick={() => {
                onViewChange(item.id);
                setSidebarOpen(false);
              }}
              compact={compact}
            />
          ))}
        </div>

        <div className={`${compact ? "px-1" : "px-2"} mt-4 space-y-0.5`}>
          <div
            className={`text-[9px] font-semibold uppercase tracking-wider text-[var(--color-text-subtle)] ${compact ? "text-center py-1.5" : "px-2 py-1.5"}`}
          >
            {compact ? "·" : "Account"}
          </div>
          {secondaryNav.map((item) => (
            <NavLink
              key={item.id}
              item={item}
              isActive={activeView === item.id}
              onClick={() => {
                onViewChange(item.id);
                setSidebarOpen(false);
              }}
              compact={compact}
            />
          ))}
        </div>
      </div>

      {/* User */}
      {!compact && (
        <div
          className="border-t px-3 py-3"
          style={{ borderColor: "var(--color-border-default)" }}
        >
          <div className="flex items-center gap-2">
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-[11px] font-bold"
              style={{
                background: "var(--color-action-subtle)",
                color: "var(--color-action-primary)",
              }}
              aria-hidden="true"
            >
              {avatarInitials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[12px] font-semibold text-[var(--color-text-primary)] truncate">
                {displayName}
              </div>
              <div className="text-[10px] text-[var(--color-text-subtle)]">
                Account ·{" "}
                {executionCapability === "authorized"
                  ? "Auto-submit enabled"
                  : "Prepare only"}
              </div>
            </div>
            <button
              onClick={() => onViewChange("profile")}
              className="p-1 rounded text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-raised)] transition-colors"
              style={{ borderRadius: "var(--radius-sm)" }}
              aria-label="Open profile settings"
            >
              <IconProfile size={14} />
            </button>
          </div>
          {feedbackUrl ? (
            <a
              href={feedbackUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-2 block text-[11px] font-medium text-[var(--color-action-primary)] hover:underline"
            >
              Share Alpha feedback
            </a>
          ) : null}
        </div>
      )}
    </>
  );

  return (
    <div
      className="flex h-screen min-h-0 overflow-hidden"
      style={{
        minHeight: "100dvh",
        height: "100dvh",
        background: "var(--color-bg-canvas)",
      }}
    >
      {/* Skip navigation */}
      <a href="#main-content" className="skip-nav">
        Skip to main content
      </a>

      {/* ── Desktop sidebar ── */}
      {!isMobile && !isTablet && (
        <nav
          className="shrink-0 flex flex-col border-r"
          style={{
            width: "var(--sidebar-width)",
            background: "var(--color-bg-surface)",
            borderColor: "var(--color-border-default)",
          }}
          aria-label="Primary navigation"
        >
          {sidebarContent(false)}
        </nav>
      )}

      {/* ── Tablet sidebar overlay ── */}
      {isTablet && sidebarOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            style={{ background: "var(--color-bg-overlay)" }}
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
          <nav
            className="fixed left-0 top-0 bottom-0 z-50 flex flex-col border-r slide-in-right"
            style={{
              width: "var(--sidebar-width)",
              background: "var(--color-bg-surface)",
              borderColor: "var(--color-border-default)",
              boxShadow: "var(--shadow-xl)",
            }}
            aria-label="Primary navigation"
          >
            <div className="absolute top-3 right-3">
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-1.5 rounded text-[var(--color-text-muted)] hover:bg-[var(--color-bg-raised)]"
                style={{ borderRadius: "var(--radius-md)" }}
                aria-label="Close navigation"
              >
                <IconClose size={16} />
              </button>
            </div>
            {sidebarContent(false)}
          </nav>
        </>
      )}

      {/* ── Mobile sidebar overlay ── */}
      {isMobile && sidebarOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            style={{ background: "var(--color-bg-overlay)" }}
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
          <nav
            className="fixed left-0 top-0 bottom-0 z-50 flex flex-col border-r slide-in-left"
            style={{
              width: "min(280px, calc(100vw - 32px))",
              background: "var(--color-bg-surface)",
              borderColor: "var(--color-border-default)",
              boxShadow: "var(--shadow-xl)",
            }}
            aria-label="Primary navigation"
          >
            <div className="absolute top-3 right-3">
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-1.5 rounded text-[var(--color-text-muted)] hover:bg-[var(--color-bg-raised)]"
                style={{ borderRadius: "var(--radius-md)" }}
                aria-label="Close navigation"
              >
                <IconClose size={16} />
              </button>
            </div>
            {sidebarContent(false)}
          </nav>
        </>
      )}

      {/* ── Main area ── */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* Topbar */}
        <header
          className="flex items-center gap-3 px-4 border-b shrink-0 z-20"
          style={{
            height: "var(--topbar-height)",
            background: "var(--color-bg-surface)",
            borderColor: "var(--color-border-default)",
            boxShadow: "var(--shadow-xs)",
          }}
        >
          {/* Tablet and mobile hamburger */}
          {(isTablet || isMobile) && (
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 -ml-1 rounded text-[var(--color-text-muted)] hover:bg-[var(--color-bg-raised)] transition-colors"
              style={{ borderRadius: "var(--radius-md)" }}
              aria-label="Open navigation menu"
              aria-expanded={sidebarOpen}
            >
              <IconMenu size={18} />
            </button>
          )}

          {/* Mobile logo */}
          {isMobile && (
            <div className="flex items-center gap-2">
              <Badge172 size="xs" />
              <BrandWordmark className="text-[12px] text-[var(--color-text-primary)]" />
            </div>
          )}

          {/* Quick navigation */}
          {!isMobile && (
            <button
              onClick={() => setCmdOpen(true)}
              className="flex items-center gap-2 px-3 py-1.5 border rounded text-[12px] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:border-[var(--color-border-strong)] transition-colors"
              style={{
                minWidth: "200px",
                background: "var(--color-bg-canvas)",
                borderColor: "var(--color-border-default)",
                borderRadius: "var(--radius-md)",
              }}
              aria-label="Open quick navigation (⌘K)"
            >
              <IconSearch size={13} className="shrink-0" />
              <span className="flex-1 text-left">Quick navigation</span>
              <kbd
                className="text-[10px] px-1.5 py-0.5 rounded"
                style={{
                  background: "var(--color-bg-raised)",
                  borderRadius: "var(--radius-xs)",
                  color: "var(--color-text-subtle)",
                }}
              >
                ⌘K
              </kbd>
            </button>
          )}

          <div className="flex-1" />

          {/* Execution state */}
          {!isMobile && <ExecutionChip capability={executionCapability} />}

          {/* Attention */}
          {!isMobile && (
            <button
              onClick={() => onViewChange("attention")}
              className="relative flex items-center gap-1.5 px-2.5 py-1.5 rounded text-[12px] font-medium text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-raised)] transition-colors"
              style={{ borderRadius: "var(--radius-md)" }}
              aria-label="Open attention queue"
            >
              <IconAttention size={14} />
            </button>
          )}
        </header>

        {/* Main content */}
        <main
          id="main-content"
          className="flex-1 min-h-0 overflow-y-auto"
          style={{
            background: "var(--color-bg-canvas)",
            paddingBottom: isMobile
              ? "calc(var(--mobile-nav-height) + env(safe-area-inset-bottom))"
              : 0,
          }}
          tabIndex={-1}
        >
          {children}
        </main>

        {/* ── Mobile bottom nav ── */}
        {isMobile && (
          <nav
            className="fixed bottom-0 left-0 right-0 flex items-center border-t z-30"
            style={{
              height:
                "calc(var(--mobile-nav-height) + env(safe-area-inset-bottom))",
              paddingBottom: "env(safe-area-inset-bottom)",
              background: "var(--color-bg-surface)",
              borderColor: "var(--color-border-default)",
              boxShadow: "0 -1px 12px rgb(0 0 0 / 0.06)",
            }}
            aria-label="Mobile navigation"
          >
            {mobileNavItems.map((item) => {
              const isActive = activeView === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => onViewChange(item.id)}
                  className={`flex-1 flex flex-col items-center justify-center gap-1 py-2 relative ${
                    isActive
                      ? "text-[var(--color-action-primary)]"
                      : "text-[var(--color-text-muted)]"
                  }`}
                  aria-current={isActive ? "page" : undefined}
                  aria-label={item.label}
                >
                  <item.Icon size={20} />
                  <span className="text-[10px] font-medium">{item.label}</span>
                </button>
              );
            })}
          </nav>
        )}
      </div>

      {/* ── Command palette ── */}
      {cmdOpen && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4"
          style={{ background: "var(--color-bg-overlay)" }}
          onClick={() => setCmdOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Command palette"
        >
          <div
            className="w-full max-w-xl border overflow-hidden fade-in"
            style={{
              background: "var(--color-bg-surface)",
              borderColor: "var(--color-border-default)",
              borderRadius: "var(--radius-xl)",
              boxShadow: "var(--shadow-xl)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex items-center gap-3 px-4 py-3 border-b"
              style={{ borderColor: "var(--color-border-default)" }}
            >
              <IconSearch
                size={14}
                className="text-[var(--color-text-muted)] shrink-0"
              />
              <h2 className="flex-1 text-[13px] font-semibold text-[var(--color-text-primary)]">
                Quick navigation
              </h2>
              <kbd
                className="text-[10px] px-1.5 py-0.5 border rounded text-[var(--color-text-subtle)]"
                style={{
                  borderColor: "var(--color-border-default)",
                  borderRadius: "var(--radius-xs)",
                  background: "var(--color-bg-raised)",
                }}
              >
                Esc
              </kbd>
            </div>
            <div className="p-2">
              <div className="text-[10px] font-semibold text-[var(--color-text-subtle)] uppercase tracking-wide px-2 py-1.5">
                Quick navigate
              </div>
              {[
                {
                  icon: IconJobs,
                  label: "Job inbox",
                  sub: "Owner-scoped match results",
                  view: "jobs" as ActiveView,
                },
                {
                  icon: IconAttention,
                  label: "Attention queue",
                  sub: "Capability status",
                  view: "attention" as ActiveView,
                },
                {
                  icon: IconApplications,
                  label: "Applications",
                  sub: "Package workflow status",
                  view: "applications" as ActiveView,
                },
                {
                  icon: IconAnalytics,
                  label: "Analytics",
                  sub: "Discovery funnel",
                  view: "analytics" as ActiveView,
                },
                {
                  icon: IconProfile,
                  label: "Candidate profile",
                  sub: "Identity, evidence, and preferences",
                  view: "profile" as ActiveView,
                },
              ].map((item) => (
                <button
                  key={item.label}
                  className="w-full flex items-center gap-3 px-2 py-2 rounded text-left transition-colors hover:bg-[var(--color-bg-raised)]"
                  style={{ borderRadius: "var(--radius-md)" }}
                  onClick={() => {
                    onViewChange(item.view);
                    setCmdOpen(false);
                  }}
                >
                  <item.icon
                    size={15}
                    className="text-[var(--color-text-muted)] shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-medium text-[var(--color-text-primary)]">
                      {item.label}
                    </div>
                    <div className="text-[11px] text-[var(--color-text-muted)]">
                      {item.sub}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div
              className="border-t px-4 py-2 flex items-center gap-4"
              style={{ borderColor: "var(--color-border-default)" }}
            >
              <span className="text-[10px] text-[var(--color-text-subtle)]">
                Esc Close
              </span>
              <span className="ml-auto text-[10px] text-[var(--color-text-subtle)]">
                ⌘K
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
